import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { map, Observable, Subject, take, takeUntil } from 'rxjs';
import { CrmService } from '../../services/crm.service';
import { AppTableSource, ColumnSortType } from '../../model/table.model';
import { tableGroupsHeaders } from '../../modules/crm/team/components/team/team.interface';
import { StackedSidebarService } from '../../services/stacked-sidebar.service';
import { SidebarType } from '../../components/layout/stacked-sidebar/stacked-sidebar.interface';
import { NotificationService } from '../../services/notifications.service';
import { IError } from '../../interfaces';
import { DEFAULT_ERROR, ERROR_MESSAGES, activeTranslations, PERMISSIONS } from '../../enum/constants';
import { IParams } from '../../model/http';
import { initItemsParams } from '../../services/channel.service';
import { DialogService } from '../../services/dialog.service';
import { BaseService } from '../../services/base.service';

@Component({
  selector: 'app-tab-groups',
  templateUrl: './tab-groups.component.html',
  styleUrl: './tab-groups.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TabGroupsComponent implements OnInit, OnDestroy {
  // Destroy
  private readonly destroy$ = new Subject<null>();

  // Var`s
  public isLoader: boolean = false;
  public visibleColumns: string[] = [];
  public itemsRequest: any;
  public timerSearch: any;
  public canManageTeam: boolean = false;

  // Table relative
  private dataSort: any;
  public dataSource$: Observable<AppTableSource> = this.crmService.itemsGroups$.pipe(
    map((v) => {
      tableGroupsHeaders.map((column) => {
        column.sort = ColumnSortType.NONE;
        if (column.value === this.dataSort?.active) {
          column.sort = this.dataSort.direction;
        }
        return column;
      });

      return {
        headers: this.getTableHeaders(),
        rows: v.items || [],
        pageSize: v?.size || 0,
        pageIndex: v.page ? v.page - 1 : 0,
        length: v.total || 0,
      };
    }),
  );

  constructor(
    private readonly crmService: CrmService,
    private readonly dialogService: DialogService,
    private readonly stackedSidebarService: StackedSidebarService,
    private readonly notificationService: NotificationService,
    private readonly baseService: BaseService,
  ) {}

  public ngOnInit(): void {
    this.canManageTeam = this.baseService.isHasPermission(PERMISSIONS.TEAM_ACTION);
    this.visibleColumns = this.getTableHeaders().map((v) => v.value);
    this.onLoadListGroups();
  }

  public ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public onLoadListGroups(): void {
    this.isLoader = true;
    this.crmService
      .loadListGroups()
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: () => {
          this.isLoader = false;
        },
        error: (e) => {
          this.isLoader = false;
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
          });
        },
      });
  }

  public getItemsWithUpdatedParams(params: IParams): void {
    if (this.itemsRequest) {
      this.itemsRequest.unsubscribe();
    }

    this.isLoader = true;
    this.itemsRequest = this.crmService
      .setQueryGroupsParams(params)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: (res: any) => {
          this.isLoader = false;
          if (!res.items.length && res.page > 1) {
            this.getItemsWithUpdatedParams({
              page: res.page - 1,
              take: res.take,
            });
          }
        },
        error: (e: any) => {
          this.isLoader = false;
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code]);
          });
        },
      });
  }

  public onCreateGroup(): void {
    if (!this.canManageTeam) return;

    this.stackedSidebarService
      .openSidebar(
        {
          type: SidebarType.CreateGroup,
          maxWidth: '90vw',
        },
        {},
      )
      .pipe(take(2))
      .subscribe((res: any) => {
        if (res === 'success') {
          this.notificationService.showSuccess(
            '',
            activeTranslations.notifications_texts.success.successfully_created,
          );
          this.onLoadListGroups();
        }
      });
  }

  public onSearchChange(search: string): void {
    if (this.timerSearch) clearTimeout(this.timerSearch);
    this.timerSearch = setTimeout(() => {
      this.getItemsWithUpdatedParams({
        ...initItemsParams,
        search: search,
      });
    }, 500);
  }

  public onSortChange(e: any): void {
    this.dataSort = e;
    this.getItemsWithUpdatedParams({
      sort: e.active,
      order: e.direction,
    });
  }

  public onPaginationChange(e: any): void {
    const { pageIndex, pageSize } = e;
    const page = pageIndex + 1;
    const take = pageSize;

    this.getItemsWithUpdatedParams({ page, take });
  }

  public onActionChange(e: any): void {
    if (!this.canManageTeam) return;

    if (e.label === 'EDIT') {
      this.stackedSidebarService
        .openSidebar(
          {
            type: SidebarType.CreateGroup,
            maxWidth: '90vw',
          },
          { data: { group_id: e.id } },
        )
        .pipe(take(2))
        .subscribe((res: any) => {
          if (res === 'success') {
            this.getItemsWithUpdatedParams({});
          }
        });
    }

    if (e.label === 'DELETE') {
      const dialogRef = this.dialogService.openConfirm({
        text: 'dialogs.tab_groups',
      });

      dialogRef.pipe(takeUntil(this.destroy$)).subscribe({
        next: (res: any) => {
          if (res) {
            this.crmService
              .deleteGroup(e.id)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => {
                  this.notificationService.showSuccess(
                    activeTranslations.notifications_texts.success
                      .successfully_deleted,
                    '',
                  );
                  this.onLoadListGroups();
                },
                error: (e: any) => {
                  (e.error?.errors || []).forEach((err: IError) => {
                    this.notificationService.showError('', ERROR_MESSAGES[err.code]);
                  });
                },
              });
          }
        },
        error: (e: any) => {
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code]);
          });
        },
      });
    }
  }

  public onRowClick(e: any): void {
    if (!this.canManageTeam) return;

    this.stackedSidebarService
      .openSidebar(
        {
          type: SidebarType.CreateGroup,
          maxWidth: '90vw',
        },
        { data: { group_id: e.id } },
      )
      .pipe(take(2))
      .subscribe((res: any) => {
        if (res === 'success') {
          this.onLoadListGroups();
        }
      });
  }

  private getTableHeaders(): any[] {
    return tableGroupsHeaders.filter((column) => this.canManageTeam || column.value !== 'action');
  }
}
